/**
 * Polar tracking / IsoDraft / SmartTrack input guides (2D POLAR+F10,
 * 3D ISODRAFT + SMARTTRACK commands).
 *
 * The runners used to flip useViewStore flags that no render layer read —
 * the commands reported "ON" but nothing appeared. This overlay closes the
 * loop:
 *
 *  - polarOn:   rays every 90° (2D default increment, matching the
 *               POLAR command report) from the current base point.
 *  - isoplane:  the 30/90/150° axis pair of the picked ISO plane (left/top/
 *               right) from the base point.
 *  - smartTrack: alignment crosshair rays through the OSNAP currently
 *               highlighted (useSnapStore.activeSnap), mirroring running/
 *               snap alignment guides.
 *
 * Implementation notes: the overlay is built once (static unit-length ray
 * meshes) and positioned imperatively in useFrame — the base point lives in
 * the dynamic-input engine's module refs (it changes on every click, not on
 * store updates), so a React-driven re-render would either miss clicks or
 * re-render per mousemove. Instead we update `position` + `visible` per
 * frame in a demand-rendered scene: zero re-renders, correct behavior.
 */

import { useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useViewStore } from '../state/commandAuxStores';
import { useSnapStore } from '../tools/snapping';
import { pixelsToWorld, type TrackingGuide } from '../tools/snapping/SnapManager';
import { SNAP_COLORS } from '../themes/colors';
import { getAnchorPoint } from '../core/dynamicInput/dynamicInputEngine';
import { useAppStore } from '../state/store';
import { useCommandInteract } from '../core/commands/commandInteract';

/** Length of the guide rays in world units (camera frames far less). */
const GUIDE_LEN = 100;
/** Acquisition mark half-size in screen pixels (Faz 11.4 — the `+` glyph). */
const ACQUISITION_PX = 5;
/** Re-render threshold for zoom-driven size changes (relative fraction). */
const ACQ_EPSILON = 0.08;
/** Tool ids whose gestures use ground-point input (guide visibility gate). */
const DRAFTING_TOOLS = new Set([
    'line', 'circle', 'rectangle', 'arc', 'polygon', 'polyline', 'ellipse',
    'text', 'mtext', 'dimlinear', 'dimaligned', 'dimradius', 'dimdiameter',
    'move', 'copy', 'rotate', 'scale', 'mirror_pick', 'offset', 'point',
    'wall', 'floor', 'roof', 'ceiling', 'grid', 'level', 'column',
]);

function isGestureActive(): boolean {
    const tool = String(useAppStore.getState().activeTool).toLowerCase();
    return DRAFTING_TOOLS.has(tool) || useCommandInteract.getState().active;
}

/** Guide ink for one tracked alignment: the acquired osnap kind's color, or
 *  the shared OTRACK green when the reference kind is unknown. */
function guideColor(guide: TrackingGuide): string {
    return (guide.type && (SNAP_COLORS as Record<string, string>)[guide.type]) || '#2fd675';
}

/**
 * The small `+` acquisition mark at the tracked osnap reference (Faz 11.4):
 * pixel-fixed through the epsilon-guarded pixelsToWorld pattern, inked in
 * the guide's snap-type color.
 */
function AcquisitionMark({ at, color }: { at: [number, number, number]; color: string }) {
    const { camera, gl } = useThree();
    const [half, setHalf] = useState(0.05);
    const point = useMemo(() => new THREE.Vector3(at[0], at[1], at[2]), [at[0], at[1], at[2]]);
    useFrame(() => {
        const next = pixelsToWorld(ACQUISITION_PX, point, camera, gl);
        setHalf((prev) => (
            Math.abs(prev - next) / Math.max(next, 1e-6) > ACQ_EPSILON ? next : prev
        ));
    });
    return (
        <group position={point} renderOrder={5}>
            <Line points={[[-half, 0, 0], [half, 0, 0]]} color={color} lineWidth={1} />
            <Line points={[[0, -half, 0], [0, half, 0]]} color={color} lineWidth={1} />
        </group>
    );
}

/** Build a merged line-segment geometry for a set of angles from origin. */
function rayGeometry(anglesDeg: number[]): THREE.BufferGeometry {
    const pts: number[] = [];
    anglesDeg.forEach((deg) => {
        const rad = (deg * Math.PI) / 180;
        pts.push(0, 0, 0, Math.cos(rad) * GUIDE_LEN, Math.sin(rad) * GUIDE_LEN, 0);
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    return geo;
}

export function PolarTrackOverlay() {
    const polarRef = useRef<THREE.LineSegments>(null);
    const isoRef = useRef<THREE.LineSegments>(null);
    const smartRef = useRef<THREE.LineSegments>(null);

    // Geometries depend only on the store-driven angle sets (React can own
    // these: they change when the user toggles POLAR/ISODRAFT).
    const polarOn = useViewStore((s) => s.polarOn);
    const isoplane = useViewStore((s) => s.isoplane);
    const isoDrafting = useViewStore((s) => s.isoDrafting);
    const smartTrack = useViewStore((s) => s.smartTrack);
    const otrackOn = useSnapStore((s) => s.objectSnapTracking);
    // The constraint rewrites guides on every tracked pointermove; subscribe
    // so the dashed segments remount with fresh geometry when they change.
    const trackingGuides = useSnapStore((s) => s.trackingGuides);

    const polarGeo = useMemo(
        () => (polarOn ? rayGeometry([0, 90, 180, 270]) : null),
        [polarOn]
    );
    // OCS IsoPlane angle pairs (Faz 7.1): Left [90,150], Top [30,150],
    // Right [30,90]. Rays are bidirectional, so the pairs draw the two
    // infinite axes of the plane. Gated on the isodraft master switch.
    const isoAngles = !isoDrafting ? []
        : isoplane === 'left' ? [90, 150]
            : isoplane === 'right' ? [30, 90]
                : isoplane === 'top' ? [30, 150] : [];
    const isoGeo = useMemo(
        () => (isoAngles.length ? rayGeometry(isoAngles) : null),
        [isoplane] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const smartGeo = useMemo(
        () => (smartTrack ? rayGeometry([0, 90, 180, 270]) : null),
        [smartTrack]
    );

    useFrame(() => {
        // Polar / iso rays ride from the dynamic-input base point and only
        // appear while a ground-point gesture is actually running.
        const anchor = (polarGeo || isoGeo) && isGestureActive() ? getAnchorPoint() : null;
        if (polarRef.current) {
            polarRef.current.visible = !!anchor;
            if (anchor) polarRef.current.position.copy(anchor);
        }
        if (isoRef.current) {
            isoRef.current.visible = !!anchor;
            if (anchor) isoRef.current.position.copy(anchor);
        }
        // SmartTrack rides the live OSNAP marker (store-owned; the snap
        // manager publishes it on every pointer move).
        const snap = smartGeo ? useSnapStore.getState().activeSnap : null;
        if (smartRef.current) {
            smartRef.current.visible = !!snap;
            if (snap) smartRef.current.position.copy(snap.position);
        }
    });

    return (
        <>
            {polarGeo && (
                <lineSegments ref={polarRef} geometry={polarGeo} renderOrder={5}>
                    <lineBasicMaterial color="#2f7bd6" transparent opacity={0.8} />
                </lineSegments>
            )}
            {isoGeo && (
                <lineSegments ref={isoRef} geometry={isoGeo} renderOrder={5}>
                    <lineBasicMaterial color="#33aa77" transparent opacity={0.8} />
                </lineSegments>
            )}
            {smartGeo && (
                <lineSegments ref={smartRef} geometry={smartGeo} renderOrder={5}>
                    <lineBasicMaterial color="#ff44aa" transparent opacity={0.8} />
                </lineSegments>
            )}
            {/* OTRACK alignment guides (Faz 4.3): dashed vectors through the
                tracked osnap reference, drawn at absolute world coordinates —
                no per-frame positioning needed, the constraint owns extents.
                Faz 11.4: each guide inks in its acquired snap kind's color
                and carries the small `+` acquisition mark at the reference. */}
            {otrackOn && trackingGuides.map((g, i) => {
                const ink = guideColor(g);
                return (
                    <group key={`${g.from.join()}-${g.to.join()}-${i}`}>
                        <Line
                            points={[g.from, g.to]}
                            color={ink}
                            lineWidth={1}
                            dashed
                            dashSize={0.25}
                            gapSize={0.15}
                            transparent
                            opacity={0.85}
                            renderOrder={5}
                        />
                        <AcquisitionMark at={g.from} color={ink} />
                    </group>
                );
            })}
        </>
    );
}
